import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database.types';

type Project = Database['public']['Tables']['projects']['Row'];
type ProjectInsert = Database['public']['Tables']['projects']['Insert'];
type ProjectUpdate = Database['public']['Tables']['projects']['Update'];


/**
 * API service for Project CRUD operations
 */
export const projectsApi = {
    /**
     * Get all projects for the current user
     */
    async getAll(): Promise<{ data: Project[] | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('projects')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get a single project by ID
     */
    async getById(id: number): Promise<{ data: Project | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('projects')
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Create a new project
     */
    async create(
        project: ProjectInsert
    ): Promise<{ data: Project | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('projects')
                .insert(project)
                .select()
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Update an existing project
     */
    async update(
        id: number,
        updates: ProjectUpdate
    ): Promise<{ data: Project | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('projects')
                .update(updates)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Delete a project
     */
    async delete(id: number): Promise<{ error: Error | null }> {
        try {
            const { error } = await supabase.from('projects').delete().eq('id', id);

            if (error) throw error;
            return { error: null };
        } catch (error) {
            return {
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },
};

